import { MaskitoOptions } from '@maskito/core';

/**
 * Client-side IBAN check for instant feedback. The backend validates again
 * (src/backend/bank_account.py) and is the one that decides.
 */
export function normalizeIban(raw: string): string {
  return (raw || '').replace(/\s+/g, '').toUpperCase();
}

/** Returns an error message, or null when the IBAN passes the mod-97 check. */
export function ibanError(raw: string): string | null {
  const iban = normalizeIban(raw);
  if (!iban) return 'Bitte gib eine IBAN an.';
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/.test(iban)) return 'Die IBAN hat ein ungültiges Format.';
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  let remainder = 0;
  for (const char of rearranged) {
    const value = char >= 'A' ? (char.charCodeAt(0) - 55).toString() : char;
    for (const digit of value) remainder = (remainder * 10 + Number(digit)) % 97;
  }
  return remainder === 1 ? null : 'Die IBAN ist ungültig (Prüfziffer stimmt nicht).';
}

/**
 * Input mask for the IBAN field: country code, check digits, then up to 30
 * letters or digits, grouped in blocks of four. Maskito keeps the caret in
 * place, skips the spaces on backspace and accepts pasted IBANs with spaces.
 */
const IBAN_MAX_LENGTH = 34;
export const IBAN_MASK: MaskitoOptions = {
  mask: Array.from({ length: IBAN_MAX_LENGTH }, (_, i) =>
    i < 2 ? /[A-Z]/ : i < 4 ? /\d/ : /[A-Z0-9]/,
  ).flatMap((char, i) => (i > 0 && i % 4 === 0 ? [' ', char] : [char])),
  preprocessors: [
    ({ elementState, data }) => ({
      elementState: { ...elementState, value: elementState.value.toUpperCase() },
      data: data.toUpperCase(),
    }),
  ],
};
